import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { fetchCountryData } from '../../features/CountrySlice/CountrySlice';
import Loading from '../Loading';
import Select from 'react-select';

export default function CountryTabs({ setCountryId }) {
  const { t, i18n } = useTranslation();
  const { countries, loading } = useSelector((state) => state.fetchCountry);
  const dispatch = useDispatch();
  const [activeId, setActiveId] = useState('all');

  useEffect(() => {
    dispatch(fetchCountryData());
  }, [dispatch, i18n.language]);

  if (loading) {
    return <Loading />;
  }

  const tabItems = [
    { id: 'all', name: t('all') },
    ...countries.map((country) => ({
      id: country.id,
      name: country.name,
    })),
  ];

  const selectOptions = tabItems.map((item) => ({
    value: item.id,
    label: item.name,
  }));

  const customStyles = {
    // the pop‑up menu container
    menu: (provided) => ({
      ...provided,
      backgroundColor: 'rgba(255, 255, 255, 0.6)',  // 80% opacity white
      backdropFilter: 'blur(4px)',                  // optional blur
    }),
    // each option in the menu
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isFocused
        ? 'rgba(59, 130, 246, 0.1)'    // 10% blue on hover
        : state.isSelected
          ? 'rgba(59, 130, 246, 0.2)'  // 20% blue when selected
          : 'transparent',
      color: state.isFocused || state.isSelected
        ? '#1E40AF'
        : '#374151',
    }),
    // the closed control (input box)
    control: (provided) => ({
      ...provided,
      backgroundColor: 'rgba(255, 255, 255, 0.9)',  // 90% opacity
    }),
    // ensure the menu renders above other elements
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  };

  return (
    <div className="w-full lg:w-10/12 mx-auto bg-white dark:bg-zinc-900/50 rounded-xl shadow-sm shadow-zinc-100 dark:shadow-none border border-zinc-200/80 dark:border-zinc-800/60 transition-all duration-300 ease-in-out">
      {/* Desktop Tabs */}
      <div className="border-b border-zinc-200/80 dark:border-zinc-800/60 hidden lg:block transition-colors duration-300">
        <nav className="flex p-4 overflow-x-auto scrollbar-hide">
          <div className="flex space-x-2 mx-auto">
            {tabItems.map((item) => {
              const isActive = item.id === activeId;
              return (
                <button
                  key={item.id}
                  onClick={() => {
                    setActiveId(item.id);
                    setCountryId(item.id);
                  }}
                  className={`
                    relative px-6 py-3 text-sm md:text-base whitespace-nowrap font-medium transition-all duration-300 ease-in-out
                    ${isActive
                      ? 'text-emerald-700 dark:text-emerald-400 font-semibold after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-emerald-600 after:animate-underline'
                      : 'text-zinc-500 dark:text-zinc-400 hover:text-emerald-600 dark:hover:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 rounded-lg'}
                    focus:outline-none text-center
                  `}
                >
                  {item.name}
                  {isActive && (
                    <span className="absolute inset-x-0 -bottom-px h-0.5 bg-green-600" />
                  )}
                </button>
              );
            })}
          </div>
        </nav>
      </div>

      {/* Mobile Select Dropdown */}
      <div className="block w-full lg:hidden p-4">
        <Select
          options={selectOptions}
          value={selectOptions.find((opt) => opt.value === activeId)}
          isSearchable={false}
          onChange={(selected) => {
            setActiveId(selected.value);
            setCountryId(selected.value);
          }}
          styles={customStyles}
          menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
          className="w-full"
        />
      </div>
    </div>
  );
}
